import React from "react";
import { Monitor, Smartphone, RefreshCw, Check } from "lucide-react";

const dashboard = [
  "HACCP system management",
  "Business and location settings",
  "Staff and permission management",
  "Inspection oversight",
  "Temperature history",
  "Corrective action monitoring",
  "Reports and compliance history",
  "Document management",
  "Regional law-pack settings",
  "Multi-location visibility",
];

const mobile = [
  "Daily checks",
  "Temperature records",
  "Cleaning checks",
  "Hygiene inspections",
  "Corrective actions",
  "Incident reporting",
  "Task completion",
  "Mobile inspection records",
  "Kitchen-floor data entry",
];

function Panel({
  icon: Icon,
  title,
  intro,
  items,
}: {
  icon: typeof Monitor;
  title: string;
  intro: string;
  items: string[];
}) {
  return (
    <div className="app-card p-6 md:p-7 h-full">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2.5 rounded-xl bg-crystal-orange/20 border border-crystal-orange/40">
          <Icon className="h-5 w-5 text-crystal-orange" />
        </div>
        <h3 className="text-white text-xl font-semibold">{title}</h3>
      </div>
      <p className="text-white/70 text-sm mb-5">{intro}</p>
      <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-white/80 text-sm">
            <Check size={15} className="text-crystal-orange mt-0.5 shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ConnectedPlatform() {
  return (
    <section id="connected-platform" className="relative">
      <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
        <div className="text-center mb-10">
          <p className="app-eyebrow mb-3">One Connected Food-Safety System</p>
          <h2 className="heading-app text-3xl md:text-4xl mb-4">One Platform. Two Ways to Work.</h2>
          <p className="text-white/75 max-w-2xl mx-auto">
            Manage your HACCP system from the Crystal HACCP web dashboard while your team completes
            daily checks, temperature records, inspections and corrective actions from the mobile app.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-6 items-stretch">
          <Panel
            icon={Monitor}
            title="Web Dashboard"
            intro="Manage the complete food-safety operation from one central dashboard."
            items={dashboard}
          />

          <div className="flex lg:flex-col items-center justify-center gap-3">
            <div className="hidden lg:block h-full w-px bg-gradient-to-b from-transparent via-crystal-orange/50 to-transparent" />
            <div className="rounded-full border border-crystal-orange/40 bg-crystal-orange/15 p-3 shrink-0">
              <RefreshCw className="h-5 w-5 text-crystal-orange" />
            </div>
            <div className="hidden lg:block h-full w-px bg-gradient-to-b from-crystal-orange/50 via-crystal-orange/50 to-transparent" />
            <span className="lg:hidden text-white/60 text-sm">Records synchronise both ways</span>
          </div>

          <Panel
            icon={Smartphone}
            title="Mobile App"
            intro="Allow staff to complete operational food-safety work wherever it happens."
            items={mobile}
          />
        </div>

        <p className="text-center text-white/75 mt-8">
          Your team records the activity. Management sees the full picture.
        </p>
        <p className="text-center text-white/55 text-sm mt-1">
          From the kitchen floor to the management dashboard, your records stay connected.
        </p>
      </div>
    </section>
  );
}
